import React, { useState, useEffect, useCallback } from 'react';
import {
  IconButton, Badge, Popover, Box, Typography, Divider, Chip,
  List, ListItem, ListItemText, Button, Tooltip, CircularProgress, Alert,
} from '@mui/material';
import { Notifications, NotificationsNone, CheckCircleOutline } from '@mui/icons-material';
import { useStomp } from '../hooks/useStomp';
import { alertsApi } from '../services/hrmsApi';

const SEV_COLOR = { critical: 'error', warning: 'warning', info: 'info' };

const STUB_ALERTS = [
  {
    id: 'stub-1',
    type: 'attrition_spike',
    severity: 'critical',
    title: 'Attrition spike — Sales',
    message: 'Monthly attrition reached 12.3%, exceeding the 10% threshold.',
    metric: { current: 12.3, threshold: 10.0, unit: '%' },
    timestamp: new Date(Date.now() - 8 * 60_000).toISOString(),
    acknowledged: false,
  },
  {
    id: 'stub-2',
    type: 'attendance_drop',
    severity: 'warning',
    title: 'Low attendance — Operations',
    message: 'Weekly attendance in Operations dropped to 71%, below the 75% floor.',
    metric: { current: 71, threshold: 75, unit: '%' },
    timestamp: new Date(Date.now() - 35 * 60_000).toISOString(),
    acknowledged: false,
  },
  {
    id: 'stub-3',
    type: 'payroll_variance',
    severity: 'warning',
    title: 'Payroll overspend — Finance',
    message: 'Payroll this month is RM 4.8M, 8% above the approved budget of RM 4.4M.',
    metric: { current: 4_800_000, threshold: 4_400_000, unit: 'RM' },
    timestamp: new Date(Date.now() - 2 * 3_600_000).toISOString(),
    acknowledged: false,
  },
  {
    id: 'stub-4',
    type: 'headcount_alert',
    severity: 'info',
    title: 'Headcount target gap — IT',
    message: 'IT department is 8 headcount below the approved Q3 target of 142.',
    metric: { current: 134, threshold: 142, unit: 'employees' },
    timestamp: new Date(Date.now() - 6 * 3_600_000).toISOString(),
    acknowledged: false,
  },
];

function relativeTime(iso) {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function AlertsPanel() {
  const [alerts,   setAlerts]   = useState([]);
  const [unread,   setUnread]   = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [isStub,   setIsStub]   = useState(false);

  useEffect(() => {
    alertsApi.getAlerts()
      .then((data) => {
        setAlerts(data);
        setUnread(data.filter((a) => !a.acknowledged).length);
      })
      .catch(() => {
        setAlerts(STUB_ALERTS);
        setUnread(STUB_ALERTS.filter((a) => !a.acknowledged).length);
        setIsStub(true);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleMessage = useCallback((destination, body) => {
    if (destination === '/topic/anomaly-alerts' && body?.id) {
      setAlerts((prev) => [body, ...prev]);
      setUnread((prev) => prev + 1);
    }
  }, []);

  useStomp({ endpoint: '/ws', topics: ['/topic/anomaly-alerts'], onMessage: handleMessage });

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    setUnread(0);
  };

  const handleClose = () => setAnchorEl(null);

  const handleAcknowledge = async (id) => {
    setAlerts((prev) => prev.map((a) => a.id === id ? { ...a, acknowledged: true } : a));
    if (!isStub) { try { await alertsApi.acknowledgeAlert(id); } catch {} }
  };

  const handleAcknowledgeAll = async () => {
    const ids = alerts.filter((a) => !a.acknowledged).map((a) => a.id);
    setAlerts((prev) => prev.map((a) => ({ ...a, acknowledged: true })));
    if (!isStub) { ids.forEach((id) => alertsApi.acknowledgeAlert(id).catch(() => {})); }
  };

  const open = Boolean(anchorEl);
  const hasUnacked = alerts.some((a) => !a.acknowledged);

  return (
    <>
      <Tooltip title="Anomaly Alerts">
        <IconButton onClick={handleOpen} color={unread > 0 ? 'error' : 'default'}>
          <Badge badgeContent={unread || null} color="error" max={9}>
            {unread > 0 ? <Notifications /> : <NotificationsNone />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 390, maxHeight: 500, borderRadius: 2, boxShadow: 6, display: 'flex', flexDirection: 'column' } }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>Anomaly Alerts</Typography>
            {alerts.length > 0 && (
              <Chip label={alerts.length} size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
            )}
          </Box>
          {hasUnacked && (
            <Button size="small" onClick={handleAcknowledgeAll}
              sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary' }}>
              Mark all resolved
            </Button>
          )}
        </Box>
        <Divider />

        {isStub && (
          <Alert severity="info" sx={{ mx: 1.5, mt: 1, py: 0.5, fontSize: '0.7rem' }}>
            Stub preview — backend alerts endpoint not yet delivered
          </Alert>
        )}

        {/* Body */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : alerts.length === 0 ? (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <NotificationsNone sx={{ fontSize: 44, color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary" mt={1}>No anomalies detected</Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ overflowY: 'auto', flex: 1 }}>
            {alerts.map((alert, i) => (
              <React.Fragment key={alert.id}>
                {i > 0 && <Divider />}
                <ListItem
                  alignItems="flex-start"
                  sx={{
                    py: 1.5, px: 2,
                    opacity: alert.acknowledged ? 0.45 : 1,
                    bgcolor: alert.acknowledged ? 'transparent' : 'action.hover',
                    transition: 'opacity 300ms, background-color 300ms',
                  }}
                  secondaryAction={
                    !alert.acknowledged && (
                      <Tooltip title="Mark resolved">
                        <IconButton size="small" edge="end" onClick={() => handleAcknowledge(alert.id)}>
                          <CheckCircleOutline fontSize="small" color="action" />
                        </IconButton>
                      </Tooltip>
                    )
                  }
                >
                  <ListItemText
                    disableTypography
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, pr: 3 }}>
                        <Chip
                          label={alert.severity}
                          color={SEV_COLOR[alert.severity] || 'default'}
                          size="small"
                          sx={{ height: 17, fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase' }}
                        />
                        <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                          {alert.title}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
                          {alert.message}
                        </Typography>
                        <Typography variant="caption" color="text.disabled" display="block" mt={0.5}>
                          {relativeTime(alert.timestamp)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
}
